import {
  MutationFunction,
  QueryClient,
  QueryKey,
  UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient as useOriginalQueryClient
} from '@tanstack/react-query';
import { ClientArgs, InitClientReturn } from '@ts-rest/core';
import { createContext, useContext } from 'react';
import contracts from './contracts';

export const ClientContext = createContext<InitClientReturn<typeof contracts, ClientArgs> | undefined>(undefined);

type RequestOptions = Omit<RequestInit, 'body'> & { body?: Record<string, unknown> | FormData };
export type QueryParams = [string] | [string, RequestOptions];

export function useClient() {
  const client = useContext(ClientContext);
  if (!client) throw new Error('Client not initialised.');
  return client;
}

export function useQueryClient() {
  const queryClient = useOriginalQueryClient();
  if (!queryClient) throw new Error('QueryClient not initialised.');
  return queryClient;
}

export function useAPIQuery<T>(
  key: QueryKey,
  queryFn: (client: InitClientReturn<typeof contracts, ClientArgs>) => Promise<T>
): [T, UseQueryResult<T>] {
  const result = useQuery<T>(key, async context => {
    if (!context?.meta?.client) throw new Error('Client not initialised.');
    const client = context.meta.client as InitClientReturn<typeof contracts, ClientArgs>;
    return await queryFn(client);
  });

  // Because we are using suspense, we can't return undefined unless specifically set with the T | undefined type
  const data = result.data as T;
  return [data, result];
}

export function useAPIMutation<V, U>(
  func: (client: InitClientReturn<typeof contracts, ClientArgs>, queryClient: QueryClient) => MutationFunction<U, V>
) {
  const queryClient = useQueryClient();
  const client = useClient();
  return useMutation<U, unknown, V>({
    mutationFn: func(client, queryClient)
  });
}
